import { useEffect, useMemo, useRef, useState } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { getSearchUiContent } from '@/lib/ui-content/search-ui-content';

type LoadingStatusRowProps = {
  locale: LocaleDetails;
  /** True once the real result is known — races the row to 100% and holds on "done" instead of cutting off mid-step. */
  isFinishing: boolean;
  onStop: () => void;
};

/** When each step line gives way to the next; the last one stays up until the page is ready. */
const STEP_ADVANCE_MS = [6000, 7000, 6000];
const TYPE_DURATION_MS = 1100;
const FINISH_TYPE_DURATION_MS = 320;
const FINISH_RACE_MS = 500;
const TICK_MS = 100;

/** The bar never reaches its end on time alone: only the real result takes it there. */
const PROGRESS_CEILING = 95;
/** Elapsed time at which the bar is halfway to its ceiling. */
const PROGRESS_HALF_WAY_MS = 10000;

/**
 * Bar position (0-100) after `elapsedMs` of waiting. The server reports no
 * progress, so the bar promises only what is true: it keeps moving, and slows
 * as the wait gets long. Hyperbolic rather than stepped, so it never sits
 * still: about 63% at 20 s, 81% at a minute, still creeping after that.
 */
export const creationProgress = (elapsedMs: number): number => {
  const t = Math.max(0, elapsedMs);
  return (PROGRESS_CEILING * t) / (t + PROGRESS_HALF_WAY_MS);
};

/**
 * Lives in the search bar's own composer footer while a page is being
 * created — replacing the price/history/mic/send row instead of a separate
 * card below the bar. One line of text, a Stop button where Send was, and a
 * bar along the card's bottom edge. The step lines are narration on a fixed
 * schedule (the real work happens server-side, out of sight); `isFinishing`
 * is the signal that the real result is in, so the row wraps up rather than
 * being cut off.
 */
export const LoadingStatusRow = ({ locale, isFinishing, onStop }: LoadingStatusRowProps) => {
  const uiContent = getSearchUiContent(locale);

  const stepTitles = useMemo(
    () => [
      uiContent.steps.step1.title,
      uiContent.steps.step2.title,
      uiContent.steps.step3.title,
      uiContent.steps.step4.title,
    ],
    [uiContent],
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [phase, setPhase] = useState<'playing' | 'finishing' | 'done'>('playing');
  const [finishProgress, setFinishProgress] = useState<number | null>(null);

  // Finishing starts from where the bar is at that moment, not from whatever
  // was in scope when its effect was created.
  const elapsedRef = useRef(elapsedMs);
  elapsedRef.current = elapsedMs;

  // Next step line on schedule; the last one stays.
  useEffect(() => {
    if (phase !== 'playing' || stepIndex >= STEP_ADVANCE_MS.length) return;
    const timer = setTimeout(() => setStepIndex((i) => i + 1), STEP_ADVANCE_MS[stepIndex]);
    return () => clearTimeout(timer);
  }, [phase, stepIndex]);

  // The bar's clock.
  useEffect(() => {
    if (phase !== 'playing') return;
    const start = Date.now() - elapsedRef.current;
    const timer = setInterval(() => setElapsedMs(Date.now() - start), TICK_MS);
    return () => clearInterval(timer);
  }, [phase]);

  // Typewriter for the active step's title.
  const activeTitle = stepTitles[stepIndex];
  useEffect(() => {
    if (phase !== 'playing') return;
    setTypedText('');
    const text = activeTitle;
    let shown = 0;
    const perChar = Math.max(TYPE_DURATION_MS / text.length, 20);
    const timer = setInterval(() => {
      shown += 1;
      setTypedText(text.slice(0, shown));
      if (shown >= text.length) clearInterval(timer);
    }, perChar);
    return () => clearInterval(timer);
  }, [phase, activeTitle]);

  // Early completion: the real result is in. Retype "Done" and race the
  // bar to 100% instead of cutting the animation off mid-climb.
  const finishedRef = useRef(false);
  useEffect(() => {
    if (!isFinishing || finishedRef.current) return;
    finishedRef.current = true;

    const captured = creationProgress(elapsedRef.current);
    setPhase('finishing');
    setFinishProgress(captured);

    const finishText = uiContent.finishingLabel ?? 'Done';
    setTypedText('');
    let shown = 0;
    const perChar = Math.max(FINISH_TYPE_DURATION_MS / finishText.length, 40);
    const typeTimer = setInterval(() => {
      shown += 1;
      setTypedText(finishText.slice(0, shown));
      if (shown >= finishText.length) clearInterval(typeTimer);
    }, perChar);

    const raceStart = Date.now();
    const raceTimer = setInterval(() => {
      const t = Math.min(1, (Date.now() - raceStart) / FINISH_RACE_MS);
      const eased = 1 - (1 - t) ** 3;
      setFinishProgress(captured + (100 - captured) * eased);
      if (t >= 1) {
        clearInterval(raceTimer);
        setPhase('done');
      }
    }, 30);

    return () => {
      clearInterval(typeTimer);
      clearInterval(raceTimer);
    };
  }, [isFinishing, uiContent.finishingLabel]);

  const progress = finishProgress ?? creationProgress(elapsedMs);
  // Once the result is in, the page opens on its own: nothing is left to stop.
  const canStop = phase === 'playing';
  const stopLabel = uiContent.stopSearch ?? 'Stop search';

  return (
    <div className="flex w-full min-w-0 items-center gap-2 pb-1.5">
      <span className="min-w-0 flex-1 truncate pl-2 font-mono text-[13px] font-semibold text-gray-900 dark:text-white">
        {typedText}
        <span
          aria-hidden="true"
          className="ml-0.5 inline-block h-3.5 w-[2px] translate-y-[2px] animate-pulse bg-cornflowerblue align-middle"
        />
      </span>
      {/* Where Send was, the same size: Send becomes Stop while the page is built. */}
      <button
        type="button"
        onClick={onStop}
        onMouseDown={(e) => e.preventDefault()}
        disabled={!canStop}
        aria-hidden={!canStop || undefined}
        tabIndex={canStop ? undefined : -1}
        aria-label={stopLabel}
        title={stopLabel}
        className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-gray-900 transition-[background-color,opacity] duration-200 hover:bg-neutral-200 dark:bg-white/10 dark:text-white dark:hover:bg-white/15 ${
          canStop ? '' : 'pointer-events-none opacity-0'
        }`}
        name="stop-search-button"
      >
        <span aria-hidden="true" className="block h-3 w-3 rounded-[3px] bg-current" />
      </button>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-4 bottom-1 h-[3px] overflow-hidden rounded-full bg-gray-200/70 dark:bg-white/10"
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-cornflowerblue to-blue-600"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
    </div>
  );
};
